import Tooltip from '@material-ui/core/Tooltip';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CommonImage from '@components/CommonImage';
import AddColor from '@components/AddColor';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess, toastWarning } from '@components/Toast';

import { getColors } from '@store/design/action';
import { getAllColors } from '@store/reducers';

import ApiClient from '@lib/apiClient';

import { isValidFile, replaceUnderLine } from '@utils/Util';

import { PANTONE_OR_HEX_CODE, SWATCH, VALID_FILE_SIZE } from '@/constants/constant';

const DEFAULT_ADD_COLOR = 'DESIGN';
const AddColorPopUp = ({
	productId,
	onAddNewColors,
	selectedColors = [],
	addColorType = DEFAULT_ADD_COLOR,
	materialId,
	setOnlyColorType = false,
	showRemovedColors = false,
	removedColorIds = [],
	setRemovedColorIds,
	isAdd = false,
	showAdd = true,
	isShowRFD = true,
	callback,
	isEdit = false,
	editColorPopUp = false,
	setEditColorPopUp,
	colorType = '',
	selectedColorEditList,
	selectedColorEditName,
	setIsOverflow,
	disabled = false,
	isEditColor = false,
	defaultColorTitle = ''
}) => {
	const solidColorTitle = defaultColorTitle || 'Write color title';
	const [searchColor, setSearchColor] = useState('');
	const [selectedColorName, setSelectedColorName] = useState('');
	const [swatchData, setSwatchData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isAddNewColor, setIsAddNewColor] = useState(false);
	const [selectedColorList, setSelectedColorList] = useState([]);
	const [isShowColorPopup, setIsShowColorPopup] = useState(false);
	const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
	const [selectedColorType, setSelectedColorType] = useState('');
	const [representedBy, setRepresentedBy] = useState(PANTONE_OR_HEX_CODE);
	const [productColors, setProductColors] = useState([]);
	const [errors, setErrors] = useState({});
	const dispatch = useDispatch();
	const colorRef = useRef(null);
	const dropdownRef = useRef(null);
	const isValidProductId = Number.isFinite(Number(productId)) && Number(productId) > 0;

	useEffect(() => {
		setSelectedColorType(colorType);
	}, [colorType]);

	useEffect(() => {
		setSelectedColorName(selectedColorEditName);
	}, [selectedColorEditName]);

	const onOutSideClick = useCallback(
		(e) => {
			const selectedElement = e.target;
			const clickedInsidePopup = !!colorRef.current?.contains(selectedElement);
			const clickedInsideDropdown = !!dropdownRef.current?.contains(selectedElement);
			const isClassExist = selectedElement?.classList?.contains('multi-color-box');

			if (clickedInsidePopup || clickedInsideDropdown || isClassExist || isAddNewColor) return;

			setIsShowColorPopup(false);
			setIsTypeDropdownOpen(false);

			if (isEditColor) {
				setEditColorPopUp(false);
				setIsOverflow && setIsOverflow(false);
			}
		},
		[isAddNewColor, isEditColor, setEditColorPopUp, setIsOverflow]
	);

	useEffect(() => {
		document.addEventListener('mousedown', onOutSideClick);
		return () => {
			document.removeEventListener('mousedown', onOutSideClick);
		};
	}, [onOutSideClick]);

	const colors = useSelector(getAllColors).map(({ id, name, code, hexCode }) => ({
		id,
		pantoneColorId: id,
		name,
		code,
		hexCode
	}));

	const getProductColors = () => {
		if (showRemovedColors) {
			ApiClient.GET('getColorsOfProduct', productId + '/colors')
				.then(({ data }) => {
					setProductColors(data);
				})
				.catch(({ response }) => {
					if (response && response.data && response.data.message) {
						toastError(response.data.message);
					}
				});
		}
	};

	const onAddColorClick = () => {
		getProductColors();
		dispatch(getColors(searchColor));
	};

	const onSelectColorType = (type) => {
		setSelectedColorType(type);
		setIsShowColorPopup(true);
		setIsTypeDropdownOpen(false);
		setIsOverflow?.(true);
	};

	const onSelectItem = (e) => {
		if (representedBy === PANTONE_OR_HEX_CODE) {
			onResetColorErrors('COLOR_LIST');
		} else {
			onResetColorErrors('SWATCH');
		}
		onChangeRepresntBy(e.target.value);
		setSelectedColorList([]);
	};
	const getFilteredColors = () => {
		let selectedColorLists = [...selectedColorList, ...selectedColors];
		return colors.filter(
			(o1) => !selectedColorLists.some((o2) => o1.pantoneColorId === o2?.pantoneColorId)
		);
	};

	useEffect(() => {
		setSelectedColorList(selectedColorEditList);
	}, [selectedColorEditList]);

	useEffect(() => {
		if (editColorPopUp) {
			dispatch(getColors(searchColor));
		}
	}, [dispatch, editColorPopUp, searchColor]);

	useEffect(() => {
		if (editColorPopUp && isEditColor) {
			if (!isValidProductId) {
				toastError('Please select a product first');
				setEditColorPopUp(false);
				setIsOverflow?.(false);
				return;
			}

			getProductColors();
			dispatch(getColors(''));
			setIsTypeDropdownOpen(true);
			setIsShowColorPopup(false);
			setIsOverflow?.(true);
		}
	}, [dispatch, editColorPopUp, isEditColor, isValidProductId, setEditColorPopUp, setIsOverflow]);

	const onColorClicks = (item) => {
		onResetColorErrors('COLOR_LIST');
		if (selectedColorType === 'SOLID') {
			setSelectedColorList([item]);
			setSelectedColorName(item.name);
		} else {
			setSelectedColorList([...selectedColorList, item]);
		}
	};

	const onUploadSwatch = async (data) => {
		setIsLoading(true);
		await ApiClient.POST('uploadDocument', data)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					setSwatchData(data);
					toastSuccess('Uploaded successfully');
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onUpload = (e, docType = 'MATERIAL_DESIGN') => {
		onResetColorErrors('SWATCH');
		let file = e.target.files[0];
		if (file) {
			if (isValidFile(file, file?.type, VALID_FILE_SIZE)) {
				let data = {
					name: file?.name,
					docMimeType: file?.type,
					documentType: docType,
					base64Str: ''
				};
				let reader = new FileReader();
				reader.readAsDataURL(file);
				reader.onload = () => {
					data.base64Str = reader.result;
					setSwatchData({ base64Str: data.base64Str });
					onUploadSwatch(data);
				};
				reader.onerror = function (error) {
					toastError(error);
				};
			} else {
				toastWarning('Please upload file less than 12mb');
			}
		}
	};
	const onSubmit = () => {
		if (isEditColor && !isValidProductId) {
			toastError('Please select a product first');
			return;
		}
		return onSubmitColor();
	};

	const onSetColorsData = (data, value) => {
		let postData = {};
		if (data.success === true) {
			postData.id = data.id;
			postData.name = value.name;
			postData.code = value.code;
			postData.hexCode = value.hexCode;
		}
		onColorClicks(postData);
	};

	const onResetPopUp = () => {
		setIsShowColorPopup(false);
		setIsTypeDropdownOpen(false);
		setRepresentedBy(PANTONE_OR_HEX_CODE);
		setSelectedColorList([]);
		setSelectedColorName('');
		setSearchColor('');
		setErrors({});
		setSwatchData(null);
		setEditColorPopUp(false);
		setIsOverflow(false);
	};
	const onChangeRepresntBy = (item) => setRepresentedBy(item);

	const onResetColorErrors = (type) => {
		if (type === 'COLOR_LIST') {
			setErrors({ ...errors, colorError: '' });
		}
		if (type === 'COLOR_TITLE') {
			setErrors({ ...errors, colorTitle: '' });
		}
		if (type === 'SWATCH') {
			setErrors({ ...errors, swatchError: '' });
		}
	};

	const isValidForm = (colorLists, colorTitle, swatch) => {
		if (colorLists.length === 0 && representedBy !== SWATCH && !setOnlyColorType) {
			setErrors({ ...errors, colorError: 'Color is required' });
			return false;
		}
		if (!colorTitle) {
			setErrors({ ...errors, colorTitle: 'Name is required' });
			return false;
		}
		if (!swatch && representedBy === SWATCH && !setOnlyColorType) {
			setErrors({ ...errors, swatchError: 'Swatch is required' });
			return false;
		}
		return true;
	};

	const onAddColorToDesign = async (body) => {
		await ApiClient.POST('addColorToDesign', body, productId)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					onResetPopUp();
					onAddNewColors(data.payload);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onAddColorToMaterial = async (body) => {
		await ApiClient.POST('materialAction', body, `${materialId}/color/add`)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					onResetPopUp();
					onAddNewColors(data);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onSetColorType = () => {
		if (selectedColorType === 'MULTI_TITLE') return 'MULTI';
		if (selectedColorType === 'AOP_TITLE') return 'AOP';
		if (selectedColorType === 'YARN_DYED_TITLE') return 'YARN_DYED';
		if (selectedColorType === 'RFD_TITLE') return 'RFD';
	};

	const onSubmitColor = () => {
		const isValid = isValidForm(selectedColorList, selectedColorName, swatchData);
		if (!isValid) return;
		const body = {
			name: selectedColorName,
			colorType: !setOnlyColorType ? selectedColorType : onSetColorType(),
			representedBy,
			compositeColorList: selectedColorType !== 'SOLID' ? selectedColorList : [],
			pantoneColorId: selectedColorType === 'SOLID' ? selectedColorList[0]?.id : null,
			hexCode: selectedColorType === 'SOLID' ? selectedColorList[0]?.hexCode : null,
			code: selectedColorType === 'SOLID' ? selectedColorList[0]?.code : null,
			swatchDocumentId: representedBy === SWATCH ? swatchData?.id : null
		};
		if (addColorType === 'MATERIAL') {
			if (isAdd) {
				callback(body);
				onResetPopUp();
			} else {
				setIsLoading(true);
				onAddColorToMaterial(body);
			}
		} else {
			setIsLoading(true);
			onAddColorToDesign(body);
		}
	};

	const onRemoveColor = (item) => {
		setSelectedColorList([...selectedColorList.filter((color) => color.id !== item.id)]);
	};

	const renderRemovedColors = () => {
		if (showRemovedColors && removedColorIds.length > 0) {
			return (
				<>
					<span className="removed-color-heading regular-14">Removed colors</span>
					{productColors
						.filter((item) => removedColorIds.includes(item.id))
						.map((item) => {
							return (
								<li
									key={`remove_color_${item.id}`}
									className="removed-color"
									onClick={() => {
										onAddNewColors(item);
										let ids = removedColorIds.filter(
											(removedId) => item.id !== removedId
										);
										setRemovedColorIds(ids);
									}}
								>
									<span
										className="cursor-pointer color-icon"
										style={{ background: `${item.hexCode}` }}
										title={item.name}
									></span>
									<span className="color-code">{item.code}</span>
								</li>
							);
						})}
				</>
			);
		}
	};

	const renderTable = () => (
		<div className="design-update-table">
			<table className="design-basic-info-table add-color-table">
				<tbody>
					<tr>
						<td colSpan="2" className="pl-12 pb-1 added-colors-column">
							<p className="color-type-name mb-0 mr-2">{replaceUnderLine(onSetColorType())}</p>
						</td>
					</tr>
					<tr>
						<td>
							<input
								type="text"
								autoComplete="off"
								placeholder="Write color title"
								maxLength={48}
								className="border-0"
								value={selectedColorName}
								autoFocus
								onChange={(e) => {
									setSelectedColorName(e.target.value);
									onResetColorErrors('COLOR_TITLE');
								}}
							/>
							{errors.colorTitle && <p className="error pl-1">{errors.colorTitle}</p>}
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	);

	return (
		<LoaderComponent loading={isLoading}>
			<div className="option col-heading">
				<div className="dropdown" ref={dropdownRef}>
					{showAdd && !(isEdit && isEditColor) && (
						<button
							className="button text-only  pl-0 "
							type="button"
							data-toggle="dropdown"
							aria-expanded="false"
							onClick={() => onAddColorClick()}
							disabled={disabled}
						>
							{!isEdit && (
								<Tooltip
									title={<span className="regular-14">Add color</span>}
									placement="top"
									arrow
								>
									<p className="semibold-14 col-heading cursor-pointer d-flex justify-content-center align-items-center">
										<CommonImage src="/icons/plus-20.svg" alt="down" />
									</p>
								</Tooltip>
							)}
							{isEdit && (
								<span className="cursor-pointer mr-2">
									<img src="/icons/edit-black-20.svg" alt="" />
								</span>
							)}
						</button>
					)}
					<ul
						className={`dropdown-menu removed-color-dropdown dropdown-menu-right shadow-lg multi-color-box ${isEditColor ? 'edit-dropdown' : ''} ${isTypeDropdownOpen ? 'show' : ''}`}
					>
						<li className="multi-color-box" onClick={() => onSelectColorType('SOLID')}>
							<span className="multi-color-box">SOLID</span>
						</li>
						{!setOnlyColorType ? (
							<>
								<li className="multi-color-box" onClick={() => onSelectColorType('MULTI')}>
									<span className="multi-color-box">MULTI</span>
								</li>
								<li
									className="multi-color-box"
									onClick={() => {
										onSelectColorType('AOP');
										setRepresentedBy(SWATCH);
									}}
								>
									<span className="multi-color-box">AOP</span>
								</li>
								<li
									className="border-bottom multi-color-box"
									onClick={() => onSelectColorType('YARN_DYED')}
								>
									<span className="multi-color-box">Y/D</span>
								</li>
							</>
						) : (
							<>
								<li
									className="multi-color-box"
									onClick={() => {
										onSelectColorType('MULTI_TITLE');
										setSelectedColorName('MULTI');
									}}
								>
									<span className="multi-color-box">MULTI</span>
								</li>
								<li
									className="multi-color-box"
									onClick={() => {
										onSelectColorType('AOP_TITLE');
										setSelectedColorName('AOP');
									}}
								>
									<span className="multi-color-box">AOP</span>
								</li>
								<li
									className="border-bottom multi-color-box"
									onClick={() => {
										onSelectColorType('YARN_DYED_TITLE');
										setSelectedColorName('Y/D');
									}}
								>
									<span className="multi-color-box">Y/D</span>
								</li>
								{isShowRFD && (
									<li
										className="border-bottom multi-color-box"
										onClick={() => {
											onSelectColorType('RFD_TITLE');
											setSelectedColorName('RFD');
										}}
									>
										<span className="multi-color-box">RFD</span>
									</li>
								)}
							</>
						)}
						{renderRemovedColors()}
					</ul>
				</div>
			</div>

			{(isShowColorPopup || (editColorPopUp && !isEditColor)) && (
				<div className="design-info-add-color-popup shadow-24dp " ref={colorRef}>
					{(setOnlyColorType && selectedColorType === 'MULTI_TITLE') ||
					selectedColorType === 'AOP_TITLE' ||
					selectedColorType === 'YARN_DYED_TITLE' ||
					selectedColorType === 'RFD_TITLE' ? (
						renderTable()
					) : (
						<div className="design-update-table">
							<table className="design-basic-info-table add-color-table">
								<tbody>
									{representedBy === SWATCH && (
										<tr>
											<td colSpan="2" className="pl-12 pb-1 added-colors-column">
												<p className="color-type-name mb-0 mr-2">
													{selectedColorType === 'YARN_DYED'
														? 'YARN DYED'
														: selectedColorType}
												</p>
											</td>
										</tr>
									)}
									{representedBy === PANTONE_OR_HEX_CODE && (
										<tr>
											<td colSpan={2} className="pl-12 pb-1 added-colors-column ">
												<div className="d-flex align-items-center">
													<p className="color-type-name mb-0 mr-2">
														{selectedColorType === 'YARN_DYED'
															? 'YARN DYED'
															: selectedColorType}
													</p>
													{selectedColorList?.map((item) => (
														<span
															className="added-color-combo"
															key={`color_${item.hexCode}`}
														>
															<span
																className="color-icon"
																style={{
																	background: item.hexCode
																}}
															/>
															<span
																className="delete-color"
																onClick={() => onRemoveColor(item)}
															>
																<img
																	src={`${process.env.PUBLIC_URL}/icons/Close-red-16.svg`}
																	alt="close"
																/>
															</span>
														</span>
													))}
												</div>
											</td>
										</tr>
									)}

									<tr className="first-row">
										<td className="dropdown-column">
											<div className="form-group">
												<select onChange={onSelectItem} value={representedBy}>
													<option value={PANTONE_OR_HEX_CODE}>Pantone</option>
													<option value={SWATCH}>Swatch</option>
												</select>
											</div>
										</td>
										<td>
											<input
												type="text"
												autoComplete="off"
												placeholder="Write color title"
												maxLength={48}
												className="border-0"
												disabled={
													!!(
														selectedColorType === 'SOLID' &&
														representedBy === PANTONE_OR_HEX_CODE
													)
												}
												value={
													selectedColorType === 'SOLID' &&
													representedBy === PANTONE_OR_HEX_CODE
														? solidColorTitle
														: selectedColorName
												}
												onChange={(e) => {
													setSelectedColorName(e.target.value);
													onResetColorErrors('COLOR_TITLE');
												}}
											/>
											{errors.colorTitle && (
												<p className="error pl-1">{errors.colorTitle}</p>
											)}
										</td>
									</tr>

									{representedBy === PANTONE_OR_HEX_CODE && (
										<tr>
											<td colSpan={2} className="search-column">
												<div className="m-0 search-filter">
													<div className="with-icon right-icon">
														<input
															type="text"
															autoComplete="off"
															placeholder="Search"
															disabled={representedBy !== PANTONE_OR_HEX_CODE}
															onChange={(e) => {
																setSearchColor(e.target.value);
																dispatch(getColors(e.target.value));
															}}
															value={searchColor}
															className="no-border"
														/>
														<span className="right-icon">
															<img
																className="cursor-pointer"
																src={`${process.env.PUBLIC_URL}/icons/search.png`}
																alt={`${process.env.PUBLIC_URL}/icons/search.png`}
															/>
														</span>
													</div>
												</div>
											</td>
										</tr>
									)}
								</tbody>
							</table>

							{representedBy === PANTONE_OR_HEX_CODE ? (
								<>
									<div className="colors-container">
										{errors.colorError && (
											<p className="error pl-1">{errors.colorError}</p>
										)}
										<div className="color-list-with-add">
											<div className="color-list-box scroll-y-label">
												<div className="add-btn">
													<button
														className="button text"
														onClick={() => setIsAddNewColor(true)}
													>
														<img
															src={`${process.env.PUBLIC_URL}/icons/add_primary.svg`}
															alt={`${process.env.PUBLIC_URL}/icons/add_primary.svg`}
														/>
														<span className="ml-1">Add color</span>
													</button>
												</div>
												{isAddNewColor && (
													<AddColor
														callBack={onSetColorsData}
														isShowCollection={true}
														onCloseModal={() => setIsAddNewColor(false)}
													/>
												)}

												{getFilteredColors()?.map((item) => (
													<div
														className="single-color-item d-flex multi-color-box"
														key={item.id}
														onClick={() => onColorClicks(item)}
													>
														<div className="one-half color-code multi-color-box">
															<span
																className="color-icon multi-color-box"
																style={{
																	background: item.hexCode
																}}
															/>
															<span className="regular-14 multi-color-box">
																{item.code}
															</span>
														</div>
														<div className="one-half color-name multi-color-box">
															<p className="regular-14 multi-color-box">
																{item.name}
															</p>
														</div>
													</div>
												))}
											</div>
										</div>
									</div>
								</>
							) : (
								<div className="colors-container">
									{errors.swatchError && <p className="error pl-1">{errors.swatchError}</p>}
									<div className="swatch-file text-center">
										{!swatchData ? (
											<div className="swatch-upload">
												<label htmlFor="swatch-upload" className="drag-upload">
													<img
														src={`${process.env.PUBLIC_URL}/icons/upload-sm.svg`}
														alt
													/>{' '}
													<br /> Swatch
												</label>
												<input
													type="file"
													className="file-upload d-none"
													id="swatch-upload"
													onChange={onUpload}
													accept="image/*"
												/>
											</div>
										) : (
											<div className="swatch-uploaded">
												<img
													src={swatchData?.docUrl || swatchData?.base64Str}
													alt="swatch"
													className="swatch-image"
												/>
												<div className="reupload-btn">
													<label htmlFor="swatch-upload" className="drag-upload">
														<img
															src={`${process.env.PUBLIC_URL}/icons/upload-gray.svg`}
															alt="upload"
														/>
													</label>
													<input
														type="file"
														className="file-upload d-none"
														id="swatch-upload"
														onChange={onUpload}
														accept="image/*"
													/>
												</div>
											</div>
										)}
									</div>
								</div>
							)}
						</div>
					)}

					<div className="footer-buttons text-right mt-2 pt-2">
						<span className="regular-14 gray_dark_02 cursor-pointer" onClick={onResetPopUp}>
							CANCEL
						</span>
						{isEditColor && (
							<span
								className="regular-14 gray_dark_02 ml-3 cursor-pointer"
								onClick={onSubmit}
								disabled={isLoading}
							>
								UPDATE COLOR
							</span>
						)}
						{!isEditColor && (
							<span
								className="regular-14 gray_dark_02 ml-3 cursor-pointer"
								onClick={onSubmit}
								disabled={isLoading}
							>
								SUBMIT
							</span>
						)}
					</div>
				</div>
			)}
		</LoaderComponent>
	);
};

export default AddColorPopUp;
